/**
 * Cursor Helper - Custom Cursor
 *
 * Declares a single web-accessible-resource entry (`assets/*.*`, `libs/*.*`,
 * `content.js`) matched to every origin with no `use_dynamic_url` — so its bundled
 * assets, JSON collections and content script are all fetchable from any page at a
 * fixed URL.
 *
 * The cursor icons under assets/icons are the cleanest signal: they are images, so
 * `probeImage` loads them without a CORS grant and survives a strict page CSP.
 *
 * Manifest verified against version 6.2.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'bogabmgabnjabjbiggmfkdocbjgackfo';

export const cursorHelper: ExtensionDefinition = {
  id: ID,
  name: 'Cursor Helper - Custom Cursor',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 300_000,
  notes:
    'Exposes assets, libs and content.js to every origin with no dynamic URL. The ' +
    'bundled icon images are conclusive and load without a CORS grant.',
  signals: [
    {
      id: 'war-grid-icon',
      kind: 'web-accessible-resource',
      description:
        'Load assets/icons/Grid.svg via <img> — a bundled UI icon exposed to every ' +
        'origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/icons/Grid.svg'),
    },
    {
      id: 'war-content-js',
      kind: 'web-accessible-resource',
      description: 'Fetch content.js — the content script exposed as a static WAR.',
      weight: 0.5,
      run: () => probeFetch(ID, 'content.js'),
    },
    {
      id: 'war-collections-json',
      kind: 'web-accessible-resource',
      description:
        'Fetch libs/collections.json — the cursor-collection manifest, a third path ' +
        'independent of the icon and content script.',
      weight: 0.5,
      run: () => probeFetch(ID, 'libs/collections.json'),
    },
  ],
};
